import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  DetailedHTMLProps,
  TextareaHTMLAttributes,
} from 'react';
import { EditorState, ContentState } from 'draft-js';
import PropTypes from 'prop-types';
import { asHTML, createEditorStateFromHTML } from 'utils';
import TextArea from '../Input/TextArea';
import { WrapperProps } from '../Input/Wrapper';

interface RichTextInputProps
  extends DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
    WrapperProps {
  parseToHTMLCallback?: (value: string) => void;
  initialValue?: string;
  name: string;
}

export interface RichTextInputMethods {
  reset: () => void;
}

const RichTextInput = forwardRef<RichTextInputMethods, RichTextInputProps>(
  ({ parseToHTMLCallback, initialValue, maxLength, ...textAreaProps }, ref) => {
    const richTextState = () => {
      return createEditorStateFromHTML(initialValue);
    };

    const [editorState, setEditorState] = useState(() => richTextState());
    const [previousHtml, setPreviousHtml] = useState<string>('');

    useEffect(() => {
      const htmlFragment = asHTML(editorState);

      if (parseToHTMLCallback && htmlFragment !== previousHtml) {
        parseToHTMLCallback(htmlFragment);
        setPreviousHtml(htmlFragment);
      }
    }, [editorState, parseToHTMLCallback, previousHtml]);

    const reset = () => {
      setEditorState(richTextState());
    };

    useImperativeHandle(ref, () => ({
      reset: () => reset(),
    }));

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContentState = ContentState.createFromText(event.target.value);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      if (maxLength) {
        const htmlContent = asHTML(newEditorState);
        if (htmlContent.length <= maxLength) {
          setEditorState(newEditorState);
        }
      } else {
        setEditorState(newEditorState);
      }
    };

    return (
      <TextArea
        {...textAreaProps}
        value={editorState.getCurrentContent().getPlainText()}
        onChange={handleInputChange}
      />
    );
  },
);

RichTextInput.displayName = 'RichTextInput';

RichTextInput.propTypes = {
  parseToHTMLCallback: PropTypes.func,
  initialValue: PropTypes.string,
};

export default RichTextInput;
